/**
 * Pure rules for showing and curating a generator's photo gallery (Phase U, addendum F-3,
 * F-4, F-11). No React, no Firestore — every screen that lists gallery or library rows uses
 * these so they agree.
 */
import { isPlateRow } from '@officehub/shared';
import type {
  GeneratorPhoto,
  GeneratorPhotoCategory,
  GeneratorPhotoSource,
} from '../../types/fieldService/generatorPhotos';

/** The human category wins over the classifier's guess (blueprint §2.1). */
export function effectiveCategory(row: GeneratorPhoto): GeneratorPhotoCategory {
  return row.category ?? row.ai?.category ?? 'unknown';
}

export type GalleryTabId = 'overview' | 'data-plate' | 'site-access' | 'all';

/** `category` drives every tab except Data plate, which filterByTab decides with isPlateRow (the value here is descriptive only). */
export const GALLERY_TABS: ReadonlyArray<{ id: GalleryTabId; label: string; category: GeneratorPhotoCategory | null }> = [
  { id: 'overview', label: 'Overview', category: 'unit-overview' },
  { id: 'data-plate', label: 'Data plate', category: 'data-plate' },
  { id: 'site-access', label: 'Site access', category: 'site-access' },
  { id: 'all', label: 'All', category: null },
];

/**
 * The rows a tab shows (and counts). "Data plate" uses the server's plate rule (isPlateRow), not the category alone: a photo the
 * report put in a plate field has no category and no AI answer, and the tab used to show "Data plate (0)" for it (ISS-652).
 * A plate-field photo whose AI guess is another category at confidence < 0.9 shows in BOTH that tab and Data plate (the server's
 * rule: a weak AI label loses to the plate field), so the tab counts can add up to more than All. Accepted, by design.
 */
export function filterByTab(rows: readonly GeneratorPhoto[], tab: GalleryTabId): GeneratorPhoto[] {
  if (tab === 'data-plate') return rows.filter((r) => isPlateRow(r));
  const category = GALLERY_TABS.find((t) => t.id === tab)?.category ?? null;
  return category === null ? [...rows] : rows.filter((r) => effectiveCategory(r) === category);
}

function millis(row: GeneratorPhoto): number {
  return row.gallery?.addedAt?.toMillis?.() ?? row.capturedAt?.toMillis?.() ?? 0;
}

/** Primary first, then the most recently added. Returns a new array. */
export function orderGallery(rows: readonly GeneratorPhoto[], primaryPhotoId: string | null | undefined): GeneratorPhoto[] {
  return [...rows].sort((a, b) => {
    if (a.id === primaryPhotoId) return -1;
    if (b.id === primaryPhotoId) return 1;
    return millis(b) - millis(a);
  });
}

/**
 * Why a library row cannot go into a multi-photo "Add to gallery" request.
 *
 * The server refuses the WHOLE request when any one row is refused (#5386), so the batch is
 * pre-filtered here. The client does not know whether the viewer counts as office (that is
 * decided server-side from token claims and HR role tags), so rows the server refuses only
 * for technicians are sent alone ('single'): office succeeds, and a technician sees the
 * server's reason on that one row. 'never' rows cannot be added by anyone.
 */
export type BatchBlocker = { kind: 'never' | 'single'; reason: string };

export function batchBlocker(row: GeneratorPhoto): BatchBlocker | null {
  if (row.state === 'gallery') return { kind: 'never', reason: 'Already in the gallery.' };
  if (row.duplicateOf != null) {
    return { kind: 'never', reason: 'A copy of another photo. Add that one instead.' };
  }
  if (row.state === 'hidden' && row.hiddenBy?.isOffice === true) {
    return { kind: 'single', reason: 'Removed by the office. Only office staff can add it back.' };
  }
  if (row.generatorConflict != null) {
    return { kind: 'single', reason: 'Its report and ticket name different units. Office staff confirm it first.' };
  }
  return null;
}

/** Plain-language text for a promote result's per-photo `skipped` reason (durableCopy.ts). */
const SKIP_REASONS: Record<string, string> = {
  'source-missing': 'The original file is gone.',
  'source-unavailable': 'The original file cannot be reached.',
  'source-out-of-scope': 'The original is stored somewhere the gallery cannot copy from.',
  'source-too-large': 'The original file is too large.',
  'too-small': 'Too small to use in the gallery.',
  'unsupported-format': 'That image format is not supported.',
  'not-an-image': 'Not an image.',
};

export function skipReasonLabel(code: string): string {
  return SKIP_REASONS[code] ?? 'Could not be added.';
}

const SOURCE_LABELS: Record<GeneratorPhotoSource, string> = {
  fsr: 'Service report',
  goformz: 'GoFormz archive',
  'appsheet-drive': 'Service log (Drive)',
  'generator-drive': 'Generator folder (Drive)',
};

export interface PhotoProvenance {
  date: string | null;
  serviceId: string | null;
  source: string;
  addedBy: string | null;
}

/**
 * F-3: when, which visit, where it came from, who put it in the gallery. The Drive
 * `description` is deliberately NOT included: who may see it is still undecided
 * (functions/src/fieldService/gallery/callables.ts header).
 */
export function provenanceOf(row: GeneratorPhoto): PhotoProvenance {
  const when = row.capturedAt?.toDate?.();
  const addedBy = row.gallery?.addedBy;
  return {
    date: when ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null,
    serviceId: row.serviceId ?? null,
    source: SOURCE_LABELS[row.source] ?? 'Unknown source',
    addedBy: !addedBy ? null : 'system' in addedBy ? 'Picked automatically' : addedBy.displayName,
  };
}

/**
 * The Storage path the library VIEWER shows full size: the gallery thumb once promoted (draw on
 * those from the gallery), otherwise the report or GoFormz-archive original (readable by
 * same-org employees under storage.rules). Never the library tile copy: drawing (F-6) needs the
 * full-size original. Drive-sourced rows have no path the client can read, so they return null.
 */
export function libraryImagePath(row: GeneratorPhoto): string | null {
  return row.gallery?.durable.thumb ?? row.sourceRef?.storagePath ?? null;
}

/**
 * The small copy of a library photo, or null when it has none yet: the gallery thumb once
 * promoted, else the library tile copy (`libraryThumb`, generatorPhotoThumbs/...).
 */
export function librarySmallPath(row: GeneratorPhoto): string | null {
  return row.gallery?.durable.thumb ?? row.libraryThumb ?? null;
}

/**
 * The Storage path a library TILE shows (the "All photos" drawer, the FSR picker): the small
 * copy when there is one (~15 KB), otherwise the original it falls back to. Null for Drive rows.
 */
export function libraryTilePath(row: GeneratorPhoto): string | null {
  return librarySmallPath(row) ?? row.sourceRef?.storagePath ?? null;
}
